<template>
  <div>
    <h3>Stock Locations</h3>

    <div v-if="isLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <template v-else>
      <div class="custom-search d-flex justify-content-between">
        <b-form-group>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search by name"
            type="text"
            class="d-inline-block"
          />
        </b-form-group>
        <b-form-group v-if="canCreateStockLocations">
          <b-button
            variant="primary"
            class="mx-1 float-right"
            size="sm"
            @click="openStockLocationModal(null)"
          >
            <span>Add stock location</span>
          </b-button>
        </b-form-group>
      </div>
      <vue-good-table
        v-if="stockLocationsTableView"
        :columns="displayColumns"
        :rows="filteredStockLocations"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'picture'">
            <img
              :src="pictureUrl(props.row.picture)"
              alt=""
              class="table-row-thumb"
            />
          </span>
          <span v-else-if="props.column.field === 'name'" class="d-inline-flex align-items-center">
            <DatatableUserText :value="props.row.name" />
            <feather-icon
              v-if="props.row.default && stockLocations.length > 1"
              icon="StarIcon"
              size="16"
              class="ml-25 text-warning"
            />
          </span>
          <span v-else-if="props.column.field === 'parent_location_id'">
            {{
              props.row.parent_location_id
                ? stockLocations.find((c) => c.id === props.row.parent_location_id)?.name
                : ''
            }}
          </span>
          <span v-else-if="props.column.field === 'action'" class="action-link-group stock-locations-action">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openStockLocationModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>{{ canChangeStockLocations ? 'View / Edit' : 'View' }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canChangeStockLocations"
                @click="openReceiveProductsModal(props.row)"
              >
                <feather-icon icon="PackageIcon" class="mr-50" />
                <span>Receive Products</span>
              </b-dropdown-item>
              <b-dropdown-item @click="openStockChangeHistoryModal(props.row)">
                <feather-icon icon="ClockIcon" class="mr-50" />
                <span>Inventory change history</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canChangeStockLocations && !props.row.default"
                :disabled="makingDefaultId === props.row.id"
                @click="makeDefaultLocation(props.row)"
              >
                <b-spinner v-if="makingDefaultId === props.row.id" small class="mr-50" />
                <feather-icon v-else icon="StarIcon" class="mr-50" />
                <span>Make default</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canChangeStockLocations && canDeleteStockLocation"
                @click="deleteStockLocation(props.row)"
              >
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <template #pagination-bottom="props">
          <TablePagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; props.perPageChanged({ currentPerPage: n }) }"
            />
        </template>
      </vue-good-table>
    </template>
    <ManageStockLocation
      :clickDate="clickDate"
      :stockLocation="stockLocation"
      :stockLocations="stockLocations"
    />
    <ReceiveProducts
      :click-date="receiveClickDate"
      :stock-location="receiveStockLocation"
    />
    <StockChangeHistory
      :click-date="historyClickDate"
      :stock-location="historyStockLocation"
    />
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import {
  BRow,
  BCol,
  BButton,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormInput,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/Components/Elements/TablePagination.vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import ManageStockLocation from '@/views/Components/ManageStockLocation.vue'
import ReceiveProducts from '@/views/Components/ReceiveProducts.vue'
import StockChangeHistory from '@/views/Components/StockChangeHistory.vue'
import { $themeConfig } from '@themeConfig'
import { canStaffCreate, canStaffChange } from '@/libs/staffPermissions'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'

const NO_IMAGE = '/static_images/no_image.png'

export default {
  components: {
    TablePagination,
    ManageStockLocation,
    DatatableCellValue,
    DatatableUserText,
    ReceiveProducts,
    StockChangeHistory,
    BRow,
    BCol,
    BButton,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BFormInput,
    BSpinner,
    BPagination,
    VueGoodTable,
  },
  data() {
    const { noUnitImage } = $themeConfig.app

    return {
      noUnitImage,
      stockLocation: null,
      clickDate: null,
      receiveStockLocation: null,
      receiveClickDate: null,
      historyStockLocation: null,
      historyClickDate: null,
      stockLocationsTableView: true,
      isLoading: true,
      makingDefaultId: null,
      searchTerm: '',
      columns: [
        {
          label: 'Picture',
          field: 'picture',
          thClass: 'table-picture-col',
          tdClass: 'table-picture-col',
          sortable: false,
        },
        {
          label: 'Name',
          field: 'name',
        },
        {
          label: 'Description',
          field: 'description',
        },
        {
          label: 'Parent location',
          field: 'parent_location_id',
        },
        {
          label: 'Action',
          field: 'action',
          width: '72px',
          thClass: 'text-center stock-locations-action-col',
          tdClass: 'text-center stock-locations-action-col',
          sortable: false,
        },
      ],
      pageLength: 25,
    }
  },
  computed: {
    ...mapGetters({
      stockLocations: 'stockLocations/stockLocations',
      authUser: 'auth/user',
    }),
    canCreateStockLocations() {
      return canStaffCreate(this.authUser, 'stock_locations')
    },
    canChangeStockLocations() {
      return canStaffChange(this.authUser, 'stock_locations')
    },
    canDeleteStockLocation() {
      return Array.isArray(this.stockLocations) && this.stockLocations.length > 1
    },
    displayColumns() {
      return this.columns
    },
    filteredStockLocations() {
      const keyword = String(this.searchTerm || '').trim().toLowerCase()
      if (!keyword) {
        return this.stockLocations
      }
      return (this.stockLocations || []).filter((location) => (
        String(location.name || '').toLowerCase().includes(keyword)
      ))
    },
  },
  async mounted() {
    await this.loadStockLocations().catch(() => {
      toastFailure('Can not load stock locations')
    })
    this.isLoading = false
  },
  methods: {
    ...mapActions({
      loadStockLocations: 'stockLocations/loadStockLocations',
      makeDefaultStockLocation: 'stockLocations/makeDefault',
      deleteStockLocationAction: 'stockLocations/deleteStockLocation',
    }),
    pictureUrl(picture) {
      if (!picture || String(picture).trim() === '') {
        return this.noUnitImage || NO_IMAGE
      }
      const p = String(picture).trim()
      if (/^https?:\/\//i.test(p) || p.startsWith('//')) {
        return p
      }
      return p.startsWith('/') ? p : `//${p}`
    },
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      })
    },
    async deleteStockLocation(stockLocation) {
      if (!this.canDeleteStockLocation) {
        toastFailure('This stock location cannot be deleted because it is the only stock location for the company.')
        return
      }

      const message = `Are you sure you want to delete "${stockLocation.name}"? This cannot be undone.`
      if (!await this.confirmationMessage(message)) {
        return
      }

      this.deleteStockLocationAction(stockLocation.id)
        .then(() => {
          toastSuccess('Stock location has been deleted.')
        })
        .catch((error) => {
          const data = error?.response?.data
          const apiMessage = data?.error?.[0] || data?.message
          toastFailure(apiMessage || 'Failed to delete the stock location.')
        })
    },
    async makeDefaultLocation(row) {
      this.makingDefaultId = row.id
      try {
        await this.makeDefaultStockLocation(row.id)
        toastSuccess(`"${row.name}" is now the default stock location.`)
      } catch {
        toastFailure('Could not set default stock location.')
      } finally {
        this.makingDefaultId = null
      }
    },
    openStockLocationModal(row) {
      this.refreshStockLocations().finally(() => {
        this.stockLocation = row
        this.clickDate = new Date()
      })
    },
    openReceiveProductsModal(row) {
      this.receiveStockLocation = row
      this.receiveClickDate = new Date()
    },
    openStockChangeHistoryModal(row) {
      this.historyStockLocation = row
      this.historyClickDate = new Date()
    },
    refreshStockLocations() {
      return this.loadStockLocations().catch(() => {
        toastFailure('Can not load stock locations')
      })
    },
  },
}
</script>

<style scoped>
.stock-locations-action-col {
  width: 72px !important;
  min-width: 72px !important;
  max-width: 72px !important;
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
}

.stock-locations-action {
  display: inline-flex;
  justify-content: center;
  width: auto;
}

.stock-locations-action :deep(.dropdown-toggle) {
  width: 40px !important;
  min-width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
</style>
